'use client';

import React from 'react';
import Link from 'next/link';
import { MapPin } from 'lucide-react';
import { motion } from 'framer-motion';

interface AuditorListCardProps {
    id: string;
    firstName: string;
    lastName: string;
    city: string;
    country: string;
    profilePictureUrl?: string;
}

export const AuditorListCard = ({
    id,
    firstName,
    lastName,
    city,
    country,
    profilePictureUrl,
}: AuditorListCardProps) => {
    return (
        <Link href={`/auditors/${id}`} className="block h-full">
            <motion.div
                className="bg-white border border-slate-200 h-full overflow-hidden flex flex-col group hover:border-slate-300 transition-colors"
                whileHover={{ y: -2 }}
                transition={{ duration: 0.2 }}
            >
                {/* Image Section */}
                <div className="relative w-full h-[180px] bg-slate-100 overflow-hidden flex-shrink-0">
                    {profilePictureUrl ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                            src={profilePictureUrl}
                            alt={`${firstName} ${lastName}`}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            onError={(e) => {
                                e.currentTarget.src = '/profile-placeholder.png';
                                e.currentTarget.classList.remove('group-hover:scale-105');
                            }}
                        />
                    ) : (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                            src="/profile-placeholder.png"
                            alt={`${firstName} ${lastName}`}
                            className="w-full h-full object-cover"
                        />
                    )}
                </div>

                {/* Content Section */}
                <div className="p-4 flex flex-col flex-grow">
                    <h3 className="text-lg font-medium text-slate-900 mb-2 group-hover:underline decoration-1 underline-offset-4">
                        {firstName} {lastName}
                    </h3>

                    <div className="flex items-center text-sm text-slate-500 gap-2">
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        <span>{city}, {country}</span>
                    </div>
                </div>
            </motion.div>
        </Link>
    );
};
